import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Brain, Check, Pencil, Trash2, X } from "lucide-react";
import { apiClient } from "../api/client";
import { confirmMemorySuggestion, dismissMemorySuggestion } from "../api/aiApi";
import { enumLabel } from "../utils/enumLabels";
import { toast } from "sonner";

// V4.23 Polish — slug-like fact VALUES (vertical_focus/transaction_type)
// pass through the central enum layer; free-text values render as-is.
const VALUE_ENUM_KINDS = { vertical_focus: "vertical", transaction_type: "transaction_type" };

function factValueLabel(key, value) {
  const kind = VALUE_ENUM_KINDS[key];
  if (kind && /^[a-z_]+$/.test(value || "")) return enumLabel(kind, value);
  return value;
}

/* V4.12 — Memory Center: "Was weiß die AI über mich?"
   Explicit vs inferred facts, editable, deletable, fully transparent. */

function FactRow({ fact, onSave, onDelete }) {
  const { t } = useTranslation("ai");
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(fact.value);
  const confirmed = fact.status === "explicit";
  return (
    <div
      data-testid={`memory-fact-${fact.key}`}
      className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3"
    >
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">
          {t(`memory.keys.${fact.key}`, { defaultValue: fact.label })}
        </div>
        {editing ? (
          <input
            data-testid={`memory-edit-input-${fact.key}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-950/70 px-2 py-1 text-sm text-zinc-100"
          />
        ) : (
          <div className="truncate text-sm text-zinc-100">
            {factValueLabel(fact.key, fact.value)}
          </div>
        )}
      </div>
      <span
        data-testid={`memory-status-${fact.key}`}
        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${
          confirmed
            ? "bg-emerald-500/15 text-emerald-300"
            : "bg-amber-500/15 text-amber-300"
        }`}
      >
        {confirmed ? t("memory.confirmed") : t("memory.inferred")}
      </span>
      <div className="flex shrink-0 gap-1">
        {editing ? (
          <>
            <button
              data-testid={`memory-save-${fact.key}`}
              onClick={() => { onSave(fact.key, value); setEditing(false); }}
              className="rounded-md p-1.5 text-emerald-300 hover:bg-emerald-500/10"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setValue(fact.value); setEditing(false); }}
              className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800"
            >
              <X className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <button
              data-testid={`memory-edit-${fact.key}`}
              onClick={() => setEditing(true)}
              className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              data-testid={`memory-delete-${fact.key}`}
              onClick={() => onDelete(fact.key)}
              className="rounded-md p-1.5 text-zinc-500 hover:bg-red-500/10 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AIMemoryPage() {
  const { t } = useTranslation("ai");
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["ai-greeting"],
    queryFn: async () => (await apiClient.get("/ai/memory")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["ai-greeting"] });

  const save = async (key, value) => {
    if (!value.trim()) return;
    try {
      await apiClient.put(`/ai/memory/${key}`, { value: value.trim() });
      toast.success(t("memory.saved"));
      refresh();
    } catch { toast.error(t("assistant.genericError")); }
  };
  const remove = async (key) => {
    try {
      await apiClient.delete(`/ai/memory/${key}`);
      toast.success(t("memory.deleted"));
      refresh();
    } catch { toast.error(t("assistant.genericError")); }
  };
  const clearAll = async () => {
    if (!window.confirm(t("memory.clearConfirm"))) return;
    try {
      await apiClient.delete("/ai/memory");
      toast.success(t("memory.cleared"));
      refresh();
    } catch { toast.error(t("assistant.genericError")); }
  };

  const facts = data?.facts || [];
  const suggestions = data?.suggestions || [];

  const confirmSuggestion = async (s) => {
    try {
      await confirmMemorySuggestion(s.key, s.value);
      toast.success(t("memory.suggestionSaved"));
      refresh();
    } catch { toast.error(t("assistant.genericError")); }
  };
  const dismissSuggestion = async (s) => {
    try {
      await dismissMemorySuggestion(s.key, s.value);
      refresh();
    } catch { toast.error(t("assistant.genericError")); }
  };

  return (
    <div data-testid="ai-memory-page" className="mx-auto max-w-2xl px-4 py-8 space-y-6">
      <div className="flex items-center gap-3">
        <Link
          to="/ai"
          data-testid="memory-back-link"
          className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="flex items-center gap-2 text-xl font-medium text-zinc-100">
            <Brain className="h-5 w-5 text-amber-400" /> {t("memory.title")}
          </h1>
          <p className="text-sm text-zinc-500">{t("memory.subtitle")}</p>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="memory-suggestions-list">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">
            {t("memory.suggestionsTitle")}
          </div>
          {suggestions.map((s) => (
            <div
              key={`${s.key}-${s.value}`}
              data-testid={`memory-suggestion-${s.key}`}
              className="flex items-center gap-3 rounded-xl border border-sky-500/25 bg-sky-500/5 px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-wider text-sky-300/70">
                  {t(`memory.keys.${s.key}`, { defaultValue: s.label })}
                </div>
                <div className="truncate text-sm text-zinc-100">{s.value}</div>
                <div className="text-[10px] text-zinc-600">
                  {t("memory.suggestionEvidence", {
                    count: s.count, days: s.window_days,
                  })}
                </div>
              </div>
              <button
                data-testid={`memory-suggestion-confirm-${s.key}`}
                onClick={() => confirmSuggestion(s)}
                className="rounded-md p-1.5 text-emerald-300 hover:bg-emerald-500/10"
                title={t("memory.suggestionYes")}
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                data-testid={`memory-suggestion-dismiss-${s.key}`}
                onClick={() => dismissSuggestion(s)}
                className="rounded-md p-1.5 text-zinc-500 hover:bg-red-500/10 hover:text-red-300"
                title={t("memory.suggestionNo")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {facts.length === 0 ? (
        <div
          data-testid="memory-empty"
          className="rounded-2xl border border-zinc-800 bg-zinc-900/40 px-5 py-8 text-center"
        >
          <p className="text-sm text-zinc-400">{t("memory.empty")}</p>
          <Link
            to="/ai"
            className="mt-3 inline-block rounded-full border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-300 hover:bg-amber-500/20"
          >
            {t("memory.emptyCta")}
          </Link>
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="memory-facts-list">
            {facts.map((f) => (
              <FactRow key={f.key} fact={f} onSave={save} onDelete={remove} />
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-zinc-800/70 pt-4">
            <p className="max-w-sm text-[11px] text-zinc-600">{t("memory.hint")}</p>
            <button
              data-testid="memory-clear-all"
              onClick={clearAll}
              className="rounded-md px-3 py-1.5 text-xs text-red-300/80 hover:bg-red-500/10"
            >
              {t("memory.clearAll")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
